// Copyright (c) Microsoft. All rights reserved.

import { useMsal } from '@azure/msal-react';
import { Body1, Spinner, Title3 } from '@fluentui/react-components';
import { FC, useEffect, useMemo, useState } from 'react';
import { renderApp } from '../../index';
import { AuthHelper, Roles } from '../../libs/auth/AuthHelper';
import { BackendServiceUrl, NetworkErrorMessage } from '../../libs/services/BaseService';
import { MaintenanceService, MaintenanceStatus } from '../../libs/services/MaintenanceService';
import { useAppDispatch, useAppSelector } from '../../redux/app/hooks';
import { RootState, store } from '../../redux/app/store';
import { setMaintenance } from '../../redux/features/app/appSlice';
import { useSharedClasses } from '../../styles';

interface IData {
    onBackendFound: () => void;
}

export const BackendProbe: FC<IData> = ({ onBackendFound }) => {
    const classes = useSharedClasses();
    const dispatch = useAppDispatch();
    const { isMaintenance } = useAppSelector((state: RootState) => state.app);
    const maintenanceService = useMemo(() => new MaintenanceService(), []);
    const { instance, inProgress } = useMsal();

    const [model, setModel] = useState<MaintenanceStatus | null>(null);
    const [attempts, setAttempts] = useState(0); // Track attempts in state
    const maxAttempts = 4; // Maximum number of attempts before redirecting to login

    const { activeUserInfo } = store.getState().app;
    const isAdmin = activeUserInfo?.roles.includes(Roles.Admin) ?? false;

useEffect(() => {
    const maxWaitTime = 10000; // Maximum wait time in milliseconds (10 seconds)
    const startTime = Date.now();

    const timer = setInterval(() => {
        const elapsedTime = Date.now() - startTime;

        if (elapsedTime >= maxWaitTime) {
            console.error('Max wait time reached. Stopping backend probe.');
            clearInterval(timer);
            setAttempts(maxAttempts); // Mark as failed
            return;
        }

        const onBackendFoundWithAuthCheck = () => {
            if (!AuthHelper.getAuthConfig()) {
                renderApp(); // Re-render the app if auth config is missing
            } else {
                onBackendFound(); // Proceed normally
            }
        };

        AuthHelper.getSKaaSAccessToken(instance, inProgress)
            .then((token) =>
                maintenanceService
                    .getMaintenanceStatus(token)
                    .then((data) => {
                        setModel(data); // App is in maintenance
                        clearInterval(timer); // Stop further retries
                    })
                    .catch((e: any) => {
                        if (e instanceof Error && e.message.includes(NetworkErrorMessage)) {
                            console.warn('Network error encountered. Retrying...');
                            return; // Retry on the next interval
                        }

                        console.error('Error fetching maintenance status:', e);
                        dispatch(setMaintenance(false)); // App is not in maintenance
                        onBackendFoundWithAuthCheck();
                    }),
            )
            .catch((error) => {
                console.error('Error getting access token:', error);
                setAttempts((prev) => prev + 1); // Increment attempts

                if (attempts + 1 >= maxAttempts) {
                    console.error('Max attempts reached. Redirecting to login.');
                    clearInterval(timer); // Stop further retries
                    AuthHelper.loginAsync(instance).catch((error) => {
                        console.error('Error during login redirect:', error);
                    }); // Redirect to login
                }
            });
    }, 2500);

    return () => {
        clearInterval(timer);
    };
}, [dispatch, maintenanceService, onBackendFound, instance, inProgress, attempts]);

return (
    <>
        {isMaintenance ? (
            <div className={classes.informativeView}>
                <Title3>{model?.title ?? 'Site undergoing maintenance...'}</Title3>
                <Spinner />
                <Body1>
                    {model?.message ?? 'Planned site maintenance is underway. We apologize for the disruption.'}
                </Body1>
                <Body1>
                    <strong>
                        {model?.note ??
                            "Note: If this message doesn't resolve after a significant duration, refresh the browser."}
                    </strong>
                </Body1>
            </div>
        ) : (
            <div className={classes.informativeView}>
                <Title3>Verbinden...</Title3>
                <Spinner />
                {attempts >= maxAttempts && (
                    <Body1>
                        Unable to connect to the backend. Please check your network or contact support.
                    </Body1>
                )}
                {isAdmin && (
                    <>
                        <Body1>
                            This app expects to find a server running at <strong>{BackendServiceUrl}</strong>
                        </Body1>
                        <Body1>
                            To run the server locally, use Visual Studio, Visual Studio Code, or type the following
                            command:{' '}
                            <code>
                                <strong>dotnet run</strong>
                            </code>
                        </Body1>
                        <Body1>
                            If running locally, ensure that you have the{' '}
                            <code>
                                <b>REACT_APP_BACKEND_URI</b>
                            </code>{' '}
                            variable set in your <b>webapp/.env</b> file
                        </Body1>
                    </>
                )}
            </div>
        )}
    </>
);
};
